import { useState, useEffect, useCallback } from "react";
import { Button, Table, Input, Loader, Popup } from "semantic-ui-react";
import { IReport } from "../../models/report";
import {
    deleteReport,
    downloadPublishedReport,
    getPublishedReports,
    unpublishReport,
} from "../../services/reportsService";
import { toast } from "../..";
import { UnpublishReportModal } from "../../containers/Report/UnpublishReportModal";
import { DeleteReportModal } from "../../containers/Report/DeleteReportModal";

interface ITableProps {
    refreshUnpublishedReports: () => void;
}

export const InternalReportsTable = ({ refreshUnpublishedReports }: ITableProps) => {
    const [loading, setLoading] = useState(false);
    const [loadingReports, setLoadingReports] = useState(true);

    const [internalReports, setInternalReports] = useState<IReport[]>([]);

    const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState<IReport>();

    const [filterText, setFilterText] = useState<string>("");

    const fetchReports = useCallback(async () => {
        setLoadingReports(true);
        try {
            let res = await getPublishedReports(true, undefined, filterText);
            let published = res.filter(r => r.final);
            setInternalReports(published);
            setLoadingReports(false);
        } catch (err) {
            toast("Unable to fetch internal questionnaires results.", false, 3000);
        } finally {
            setLoadingReports(false);
        }
    }, [filterText]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleUnpublishReport = async () => {
        if (!selectedReport?.id) {
            return;
        }
        setLoading(true);
        try {
            await unpublishReport(selectedReport.id);
            toast("Report successfully unpublished", true, 3000);
            fetchReports();
            refreshUnpublishedReports();
        } catch (err) {
            toast("Unable to unpublish report", false, 3000);
        } finally {
            setLoading(false);
            setUnpublishModalOpen(false);
            setSelectedReport(undefined);
        }
    };

    const handleDeleteReport = async () => {
        if (!selectedReport?.id) {
            return;
        }
        setLoading(true);
        try {
            await deleteReport(selectedReport.id);
            toast("Report successfully deleted", true, 3000);
            fetchReports();
        } catch (err) {
            toast("Unable to delete report", false, 3000);
        } finally {
            setDeleteModalOpen(false);
            setSelectedReport(undefined);
            setLoading(false);
        }
    };

    const handleDownloadReport = async (report: IReport) => {
        if (!report.id) {
            return;
        }
        setLoading(true);
        try {
            await downloadPublishedReport(report);
            toast("Report successfully downloaded", true, 3000);
        } catch (err) {
            toast("Unable to download report", false, 3000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loadingReports || loading ? (
                <Loader active={loadingReports} inline="centered" />
            ) : null}

            <Table fixed className="forms-overview" columns={3}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>
                            <Input
                                fluid
                                value={filterText}
                                onChange={e => setFilterText(e.target.value)}
                                icon="search"
                            />
                        </Table.HeaderCell>
                    </Table.Row>
                    <Table.Row>
                        <Table.HeaderCell>Name</Table.HeaderCell>
                        <Table.HeaderCell>Date created</Table.HeaderCell>
                        <Table.HeaderCell></Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                <Table.Body>
                    {internalReports?.map(report => {
                        return (
                            <Table.Row key={report.id}>
                                <Table.Cell
                                    className="clickable"
                                    onClick={() => handleDownloadReport(report)}
                                >
                                    {report.title}
                                </Table.Cell>
                                <Table.Cell>{report.date}</Table.Cell>

                                <Table.Cell textAlign="right">
                                    <Popup
                                        content="Unpublish internal questionnaires results"
                                        trigger={
                                            <Button
                                                primary
                                                icon="eye slash"
                                                onClick={() => {
                                                    setUnpublishModalOpen(true);
                                                    setSelectedReport(report);
                                                }}
                                            />
                                        }
                                    />
                                    <Popup
                                        content="Delete internal questionnaires results"
                                        trigger={
                                            <Button
                                                primary
                                                icon="delete"
                                                onClick={() => {
                                                    setDeleteModalOpen(true);
                                                    setSelectedReport(report);
                                                }}
                                            />
                                        }
                                    />
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
            <Loader active={loadingReports} inline="centered" />

            <UnpublishReportModal
                unpublishModalOpen={unpublishModalOpen}
                setUnpublishModalOpen={setUnpublishModalOpen}
                handleReportUnpublish={handleUnpublishReport}
            />
            <DeleteReportModal
                openModal={deleteModalOpen}
                setOpenModal={setDeleteModalOpen}
                handleDeleteReport={handleDeleteReport}
                reportId={selectedReport?.id ?? ""}
            />
        </>
    );
};
